import React from "react";
import { useQuery } from "@apollo/client";
import { TOP_PROJECTS_BY_POLES } from "../../graphql/gqlProject";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Typography,
  Chip,
  CircularProgress,
  Toolbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

type TopProject = {
  id: string;
  name: string;
  status: "PRIVATE" | "SHARED" | "PUBLIC";
  polesCount: number;
  owner?: string | null;
  ownerId?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type TopResp = {
  getTopProjectsByPoles: TopProject[];
};

const statusChipColor = (s: TopProject["status"]) =>
  s === "PUBLIC" ? "success" : s === "SHARED" ? "warning" : "primary";

const TopProjectsByPolesTable: React.FC = () => {
  const navigate = useNavigate();
  const [limit, setLimit] = React.useState<number>(10);

  const { data, loading, error, refetch } = useQuery<TopResp>(
    TOP_PROJECTS_BY_POLES,
    { variables: { limit } }
  );

  const rows = data?.getTopProjectsByPoles ?? [];

  const handleRowOpen = (r: TopProject) => {
    if (!r.ownerId) return;
    navigate(`/${r.ownerId}/projects/${r.id}`);
  };

  return (
    <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
      <Toolbar sx={{ gap: 2, flexWrap: "wrap" }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Top Projects by Poles
        </Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="limit-label" color="secondary">
            Limit
          </InputLabel>
          <Select
            labelId="limit-label"
            label="Limit"
            value={limit}
            onChange={(e) => {
              const v = Number(e.target.value);
              setLimit(v);
              refetch({ limit: v });
            }}
            color="secondary"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
      </Toolbar>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 2 }}>
          <Typography color="error">
            {error.message || "Failed to load table"}
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Poles</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, idx) => {
                const disabled = !r.owner;
                return (
                  <TableRow
                    hover
                    key={r.id}
                    onClick={() => !disabled && handleRowOpen(r)}
                    onKeyDown={(e) => {
                      if (!disabled && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        handleRowOpen(r);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={
                      r.owner
                        ? `Open project ${r.name}`
                        : `Project ${r.name} has no owner id`
                    }
                    sx={{
                      cursor: disabled ? "default" : "pointer",
                    }}
                  >
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{r.name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={statusChipColor(r.status)}
                        label={r.status}
                      />
                    </TableCell>
                    <TableCell align="right">{r.polesCount}</TableCell>
                    <TableCell>{r.owner ?? "—"}</TableCell>
                    <TableCell>
                      {r.createdAt
                        ? new Date(r.createdAt).toLocaleDateString("nl-BE", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {r.updatedAt
                        ? new Date(r.updatedAt).toLocaleDateString("nl-BE", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })
                        : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography
                      sx={{ p: 2, textAlign: "center", opacity: 0.7 }}
                    >
                      No data
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default TopProjectsByPolesTable;
